import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

// Reusable chat with any bot. sessionKey keeps history stable per user+bot(+deal).
export const BotChat = ({ bot, dealId, accent = "red", greeting }) => {
  const storageKey = `hh_chat_${bot}_${dealId || "global"}`;
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(storageKey, id);
    return id;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/bots/chat/${sessionId}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/bots/chat", { bot, message: text, session_id: sessionId, deal_id: dealId });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond right now." }]);
    } finally {
      setLoading(false);
    }
  };

  const accentMap = { amber: "text-amber-300", indigo: "text-indigo-300", teal: "text-amber-300" };
  const accentText = accentMap[accent] || accentMap.teal;

  return (
    <div className="flex flex-col h-80 rounded-lg border border-white/10 bg-[#0B0E14]">
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid={`chat-${bot}`}>
        {messages.length === 0 && (
          <p className="text-xs text-slate-500 text-center pt-6">{greeting || "Ask me anything about this deal."}</p>
        )}
        {messages.map((m, i) => (
          <div key={`${m.created_at || "local"}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-amber-600 text-white font-medium"
                  : "bg-[#1A202C] text-slate-200 border border-white/10"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className={`rounded-xl px-3 py-2 text-sm bg-[#1A202C] border border-white/10 ${accentText} animate-pulse`}>
              thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-2.5 border-t border-white/10 flex gap-2">
        <Input
          data-testid={`chat-input-${bot}`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="bg-[#121721] border-white/10 text-white h-9 focus-visible:ring-amber-500"
        />
        <Button
          data-testid={`chat-send-${bot}`}
          onClick={send}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-3 rounded-lg"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
